import classnames from "classnames";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Header } from "@/components/Header";
import { Section, SubSection, TSubSectionRole } from "@/components/Section";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { getProfileRole, IConsultantProfile, IProfileCompleteness } from "@/data/consultant-profiles";
import { formatFullName } from "@/data/persons";
import { Flex } from "@/eworkitComponents";
import { RouteButton } from "@/infrastructure/Route";
import { Avatar } from "@/views/components/Person";
import { router } from "@/views/router";

import { LevelBadge, LevelBar } from "./components";

import "./_level.scss";

const BASE_CLASS = "completeness-level";

interface ILevelWithProfileProps {
  profile: IConsultantProfile;
}

export const LevelWithProfile: FC<ILevelWithProfileProps> = memo(({ profile }) => {
  const { t } = useTranslation(["session"]);
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const {
    completeness: { level, score },
  } = profile;

  return (
    <Section
      className={BASE_CLASS}
      background="background-card"
      borderRadius
      flex
      direction={isSmallScreen ? "column" : "row"}
      justify="space-between"
      align="center"
      innerPadding="md"
      innerMargin="md"
      padding={["lg"]}
      shadow="none">
      <Flex width={{ xxs: 12, sm: 4 }} innerMargin="md" align="center">
        <Flex align="center">
          <Avatar
            avatar={profile.avatar}
            firstName={profile.firstName}
            lastName={profile.lastName}
            size={isSmallScreen ? "small" : "medium"}
          />
        </Flex>
        <Flex grow={1} direction="column">
          <Header size="md" weight="bold" className="-word-break-all" hjSuppress>
            {formatFullName(profile)}
          </Header>
          <Text>{t(`CONSULTANT_PROFILES.DETAIL.PROFILE_ROLE.${getProfileRole(profile.companyRole)}`)}</Text>
        </Flex>
      </Flex>
      <Flex
        width={{ xxs: 12, sm: 4 }}
        direction="column"
        justify="space-between"
        innerMargin="xs"
        border={isSmallScreen ? [true, false, false] : [false]}>
        <LevelBar level={level} score={score} />
      </Flex>
      <Flex
        width={{ xxs: 12, sm: 4 }}
        align="center"
        justify={isSmallScreen ? "space-between" : "flex-end"}
        innerMargin="lg"
        border={isSmallScreen ? [true, false, false] : [false]}>
        <LevelBadge level={level} score={score} variant={isSmallScreen ? "small" : "medium"} />
      </Flex>
    </Section>
  );
});

interface ILevelWidgetProps extends IProfileCompleteness {
  consultantProfileId: IConsultantProfile["id"];
  role?: TSubSectionRole;
}

export const LevelWidget: FC<ILevelWidgetProps> = memo(({ consultantProfileId, level, score, role }) => {
  const { t } = useTranslation(["session"]);

  return (
    <SubSection
      role={role}
      direction="column"
      background="background-card"
      className={classnames(BASE_CLASS, `${BASE_CLASS}--widget`)}
      flex
      shadow="none">
      <Flex
        className={`${BASE_CLASS}-badge-wrapper`}
        align="center"
        justify="space-between"
        innerMargin="lg"
        padding={["lg", "lg", "sm", "lg"]}>
        <LevelBadge score={score} level={level} variant="widget" />
      </Flex>
      <Flex direction="column" padding={["xl", "lg", "lg"]} innerPadding="md">
        <LevelBar level={level} score={score} />
        <Flex justify="center" innerMargin="xl" direction="column">
          <RouteButton
            display="block"
            path={router.get("consultant-profiles.completeness", { consultantProfileId }).path}
            variant="secondary"
            size="lg">
            <Text size="lg">{t("CONSULTANT_PROFILES.COMPLETENESS.BUTTON_MORE")}</Text>
          </RouteButton>
        </Flex>
      </Flex>
    </SubSection>
  );
});
